/**
 * CONFIG.JS
 *
 * ReactJS Run-time Configuration Service
 *
 * Contains a configuration class that can be accessed via
 * `window.typeaheadConfig.service`:
 *
 * window.typeaheadConfig.service = window.typeaheadConfig.service || [];
 * window.typeaheadConfig.service.push((service) => {
 *    // access service API methods on `Config` clas
 * });
 *
 * HOC provides standard ReactJS configuration + global
 * overrides that can be set from `window.typeaheadConfig.service`
 *
 **/

import React from 'react';
import PropTypes from 'prop-types';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import {SVGIcon} from '@homeaway/react-svg';
import {SEARCH__24} from '@homeaway/svg-defs';

import {TypeaheadContainer} from './container';

/**
 * Merge results from a and b without causing any side effects to a or b
 * @param {*} a
 * @param {*} b
 */
export function simpleMerge(a, b) {
    let result = {...a, ...b};
    if (a.messages && b.messages) {
        result.messages = {
            ...a.messages,
            ...b.messages
        };
    }
    return result;
}

// Singleton Configuration Class.  Implemented by <withConfig> HOC.
// This is an internal class.  Only one instance of it is exported
class _Config {
    // Contains state from run-time configuration and static configuration.
    mergedState = {};
    accessMap = {};
    reactInstances = [];
    reactInstanceId = 0;

    __reset = () => {
        this.mergedState = {};
        this.accessMap = {};
        this.reactInstances = [];
        this.reactInstanceId = 0;
    };

    constructor() {
        this.initialize();
        return this;
    }

    initialize() {
        // Create namespace access
        let namespace = this.getNamespace();
        // Create globally accessible service
        namespace.service = namespace.service || [];
        // Process service access requests
        if (typeof namespace.service.forEach === 'function') {
            namespace.service.forEach((serviceCall) => {
                serviceCall(this.getPublicApi());
            });
        }
        namespace.service = {
            push: (fn) => {
                fn.call(this, this.getPublicApi());
            }
        };
    }

    /**
     * registerReactInstance()
     * Registers a react instance to faciliate syncing from global API
     * @param {*} inst
     */
    registerReactInstance(inst) {
        // Save reference to instance of ReactJS class that we are wrapping.
        this.reactInstances.push({
            id: ++this.reactInstanceId,
            inst
        });
        return ((id) => {
            // Return function to remove instance
            return () => {
                this.reactInstances.forEach((reactInstance, index) => {
                    if (reactInstance.id === id) {
                        this.reactInstances.splice(index, 1);
                        return true;
                    }
                    return false;
                });
            };
        })(this.reactInstanceId);
    }

    /**
     * Helper method to determine if we are in a SSR context
     */
    isSSR = () => {
        return (
            typeof global !== 'undefined' &&
            typeof global.typeaheadConfig === 'object'
        );
    };

    /**
     * Helper method to determine if we are in a client context
     */
    isClient = () => {
        return typeof window !== 'undefined';
    };

    /**
     * getNamespace()
     * Helper method for getting global configuration.
     * `global.typeaheadConfig` - namespace used during SSR
     * `window.typeaheadConfig` - namespace used in rendered markup
     */
    getNamespace = () => {
        if (this.isSSR()) {
            return global.typeaheadConfig; // SSR
        } else if (this.isClient()) {
            window.typeaheadConfig = window.typeaheadConfig || {};
            return window.typeaheadConfig; // Client
        }
        // Unable to reconcile environment
        return {};
    };

    /**
     * setNamespace()
     * Helper method to set data on namespace
     * @param {Object} - object value
     */
    setNamespace = (value) => {
        if (this.isSSR()) {
            global.typeaheadConfig = value;
            return value;
        } else if (this.isClient()) {
            window.typeaheadConfig = value;
            return value;
        }
        // Unable to reconcile environment
        return {};
    };

    /**
     * isObject()
     * Helper method to test for object
     */
    isObject = (obj) => {
        return typeof obj === 'object' && obj !== null;
    };

    /**
     * onFirstAccess()
     * Helper method for option access detection.  Helps with designing AB test exposures
     *
     * @example
     *
     * Here is an example of enabling Google failover test and calculating exposure its exposure from global config:
     *
     * window.typeaheadConfig.service = window.typeaheadConfig.service || [];
     * window.typeaheadConfig.service.push((service) => {
     *   service.setConfig('enableGoogleSuggestions', true | false); // Enable or Disable feature based on test
     *   service.onFirstAccess('enableGoogleSuggestions', () => {
     *      window.__ABACUS__ = window.__ABACUS__ || [];
     *      window.__ABACUS__.push('HA_typeahead_Google_failover');
     *   });
     * });
     */
    onFirstAccess = (key, fn) => {
        if (this.accessMap[key]) {
            console.error(`accessMap already contains ${key}`); // eslint-disable-line no-console
            return false;
        }
        this.accessMap[key] = () => {
            delete this.accessMap[key];
            fn();
        };
        return true;
    };

    /**
     * checkFirstAccess()
     * Helper method for option access detection.  Helps with designing AB test exposures
     */
    checkFirstAccess = (key) => {
        if (typeof this.accessMap[key] !== 'undefined') {
            this.accessMap[key]();
            return true;
        }
        return false;
    };

    /**
     * getPublicAPI()
     * Helper method to choose which methods get exposed via API
     */
    getPublicApi = () => {
        return {
            sync: this.sync,
            getConfig: this.getConfig,
            setConfig: this.setConfig,
            getMergedState: this.getMergedState,
            onFirstAccess: this.onFirstAccess
        };
    };

    /**
     * access()
     * Helper method for safe access
     * @param {*} path
     * @param {*} namespace
     */
    access(path, namespace = this.getNamespace()) {
        let properties = path.split('.');
        let currentObject = namespace;
        let property;
        while (properties.length) {
            property = properties.shift();
            if (
                !currentObject ||
                typeof currentObject[property] === 'undefined'
            ) {
                break;
            }
            if (!properties.length) {
                this.checkFirstAccess(path); // Check our access map
                return currentObject[property];
            }
            currentObject = currentObject[property];
        }
        console.error(`Could not access "${path}" in configuration`); // eslint-disable-line no-console
        return null;
    }

    /**
     * sync()
     * Invokes .setState() on wrapped reactJS instance with
     * merged state from this class.
     */
    sync = () => {
        this.reactInstances.forEach((entry) => {
            entry.inst.setWithConfig();
        });
    };

    /**
     * getConfig()
     * Get configuration data from namespace
     */
    getConfig = (path) => {
        if (typeof path === 'undefined') {
            return simpleMerge({}, this.getNamespace());
        }
        return this.access(path);
    };

    /**
     * setConfig()
     * Set configuration data at namespace
     */
    setConfig = (path, value) => {
        if (typeof value === 'undefined') {
            return this.setNamespace(simpleMerge(this.getNamespace(), path));
        }
        let properties = path.split('.');
        let currentObject = this.getNamespace();
        let property;
        while (properties.length) {
            property = properties.shift();
            if (!this.isObject(currentObject[property])) {
                currentObject[property] = {};
            }
            if (!properties.length) {
                currentObject[property] = value;
            }
            currentObject = currentObject[property];
        }
        return currentObject;
    };

    /**
     * getMergedState()
     * Read combined state so `store.js` can access all options convientiently
     */
    getMergedState = (path) => {
        if (typeof path === 'undefined') {
            return simpleMerge({}, this.mergedState);
        }
        return this.access(path, this.mergedState);
    };

    /**
     * setMergedState()
     * Called everytime the HOC wrapper mutates it's state
     */
    setMergedState = (state) => {
        this.mergedState = simpleMerge(this.mergedState, state);
    };
}

// Singleton instance
export const Config = new _Config();

export class TypeaheadProps extends React.Component {
    // in alphabetical order
    static propTypes = {
        /** action_location to be logged in edap events */
        actionLocation: PropTypes.string,
        /** App prefix for running application */
        appPrefix: PropTypes.string,
        /** Base URL for the brand */
        baseUrl: PropTypes.string,
        /** Instance of configuration class */
        config: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** Google place API key to be used when Google fallback is enabled */
        googlePlaceApiKey: PropTypes.string,
        /** Google place API Url to use when using Google fallback */
        googlePlaceApiUrl: PropTypes.string,
        /** When set to false, do not render icon.  Supports custom element.  Defaults to magnifying glass (<SVGIcon markup={SEARCH__24}). */
        icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
        /** Provided by determineIfMobile HOC.  Used to determine number of suggestion results */
        isMobile: PropTypes.bool,
        /** Option to set input focus.  Requires you to implement `onBlur` prop to update to false. */
        isTypeaheadInputFocus: PropTypes.bool,
        /** Which locale to use (SERVER SIDE ENABLED) */
        locale: PropTypes.string,
        /** highlight matched or highlight not matched */
        highlightMode: PropTypes.oneOf(['match', 'non-match']),
        /** contiguous or non-contiguous character matching for highlighting */
        matchMode: PropTypes.oneOf(['contiguous', 'non-contiguous']),
        /** Translated message strings (SERVER SIDE ENABLED) */
        messages: PropTypes.shape({
            resultsfound: PropTypes.string,
            noresultsfound: PropTypes.string,
            destinationplaceholdermsg: PropTypes.string,
            destinationselected: PropTypes.string,
            recentsearches: PropTypes.string,
            where: PropTypes.string,
            recentlyviewed: PropTypes.string,
            clearsearch: PropTypes.string
        }),
        /** Minimum number of characters to type before results are grabbed */
        minLength: PropTypes.number,
        /** Handler to be called when input has lost focus.  */
        onBlur: PropTypes.func,
        /** Handler to be called when input receives focus.  */
        onFocus: PropTypes.func,
        /** Handle to be called when typeahead config wrapper has been mounted on the DOM */
        onMount: PropTypes.func,
        /** Handler to be called when search selected  */
        onSearchSelected: PropTypes.func,
        /** Option to set popover max-width on desktop. Will get reduced to fit within viewport if necessary  */
        popoverDesktopMaxWidth: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]),
        /** Option to set min margin between right edge of popover and right edge of viewport. Defaults to baseline container outer gutter  */
        popoverDesktopMinMarginRight: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]),
        /** Option to set popover width on desktop. Will get reduced to fit within viewport if necessary  */
        popoverDesktopWidth: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]),
        /** Option to set popover min-width on desktop. Will get reduced to fit within viewport if necessary  */
        popoverDesktopMinWidth: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]),
        /** When greater than 0, caps maximum numbers of results displayed.   */
        popoverMaxDisplayCount: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]),
        /** When greater than 0, caps maximum numbers of results displayed when isMobile.   */
        popoverMaxDisplayCountMobile: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]),
        /** Limit amount of recent searches */
        recentSearchesDisplayMax: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string
        ]),
        /** The XHR path to be used when querying typeahead suggestions. */
        remotePath: PropTypes.string,
        /** When set to false, user input (IE "Aus") will be preserved when selecting a destination (IE like "Austin, TX, USA") when element is focused */
        retainUserInput: PropTypes.bool,
        /** Prepopulated Query */
        search: PropTypes.string,
        /** When set to false, it won't suggest a destination within the destination field. Defaults to true. */
        showInlineSuggestion: PropTypes.bool,
        /** Option to control whether or not user sees recent searches in typeahead list */
        showRecentSearches: PropTypes.bool,
        /** When set to true the suggestion will have type icon on the left side */
        showResultIcons: PropTypes.bool,
        /** When set to true the suggestion will have type label on the right side */
        showResultLabels: PropTypes.bool,
        /** When set to true the spinner will appear when loading destinations */
        showSpinner: PropTypes.bool,
        /** Show trending suggestions when userinput is empty and no characters was typed before*/
        showTrendingSuggestions: PropTypes.bool,
        /** Moniker brand: homeaway_us, vrbo, abritel, etc (SERVER SIDE ENABLED) */
        site: PropTypes.string,
        /** Flag to ellipsis term to fit within single-line result row. Will not affect icon, inline filter text, recently viewed, or type label */
        truncateResultTerms: PropTypes.bool,
        /** Use animation for results popover*/
        usePopoverAnimation: PropTypes.bool,
        /**
         * Use animation for input type-ahead results
         * NOTE: requires V2 remotePath
         */
        useResultsAnimation: PropTypes.bool
    };

    // in alphabetical order
    static defaultProps = {
        actionLocation: '',
        appPrefix: '/',
        baseUrl: null,
        highlightMode: 'match',
        googlePlaceApiKey: 'AIzaSyAYkFHPo1vhSXLRTZS3Z7E75XwNhaNVDEQ',
        icon: <SVGIcon markup={SEARCH__24} />,
        isMobile: false,
        isTypeaheadInputFocus: false,
        locale: null,
        matchMode: 'contiguous',
        messages: {},
        minLength: 2,
        onBlur: () => {},
        onFocus: () => {},
        onMount: () => {},
        onSearchSelected: () => {},
        popoverDesktopMaxWidth: 'none',
        popoverDesktopMinMarginRight: 30,
        popoverDesktopMinWidth: 'auto',
        popoverDesktopWidth: 'auto',
        popoverMaxDisplayCount: 8,
        popoverMaxDisplayCountMobile: 8,
        recentSearchesDisplayMax: 2,
        remotePath: '/geo/v2/typeahead/suggest',
        retainUserInput: true,
        search: '',
        showInlineSuggestion: true,
        showRecentSearches: true,
        showResultIcons: true,
        showResultLabels: true,
        showSpinner: false,
        showTrendingSuggestions: false,
        site: null,
        truncateResultTerms: true,
        usePopoverAnimation: false,
        useResultsAnimation: false
    };
}

export function withConfig(Wrapped) {
    return class WrappedWithConfig extends TypeaheadProps {
        // Holds local state
        state = {};

        /* istanbul ignore next */
        constructor(props) {
            super(props);
            this.config = Config;
            if (props.usePropsOnly !== true) {
                this.state = simpleMerge(props, this.config.getConfig());
            } else {
                this.state = simpleMerge({}, props);
            }
            this.config.setMergedState(this.state);
        }
        componentDidMount() {
            this.unregisterGlobalConfig = Config.registerReactInstance(this);
            this.props.onMount();
        }

        componentWillUnmount() {
            this.unregisterGlobalConfig();
            this.config = null; // dereference global `Config` to allow GC
        }
        /**
         * componentWillReceiveProps()
         * @param {*} nextProps
         */
        componentWillReceiveProps(nextProps) {
            this.setWithConfig(nextProps);
        }

        /**
         * setWithConfig()
         * @param {*} nextProps
         * Merges incoming props and global state into local `this.state`
         */
        setWithConfig = (nextProps = {}) => {
            let nextState = null;
            if (this.props.usePropsOnly !== true) {
                nextState = simpleMerge(nextProps, this.config.getConfig());
            } else {
                nextState = nextProps;
            }
            // Kicks off life cycle to re-render this component and
            // pass data into <Wrapped> via {...this.state}
            this.setState(nextState);

            // Update merged state in configuration service
            this.config.setMergedState(nextState);
        };
        render() {
            return <Wrapped {...this.state} config={this.config} />;
        }
    };
}

export default determineIfMobile(withConfig(TypeaheadContainer), 767);
